import { FastlyImage } from '@components/common/image'

export type TldrPoint = {
  prefix: React.ReactNode
  highlight?: React.ReactNode
  suffix?: React.ReactNode
}

type Props = {
  image: string
  imageAlt: string
  imageWidth?: number
  imageHeight?: number
  eyebrow: React.ReactNode
  /** Render the stylised "TL;DR" lettering instead of a plain heading. */
  showTldrLettering?: boolean
  heading?: React.ReactNode
  subheading?: React.ReactNode
  points: TldrPoint[]
  highlightClassName?: string
  /** Optional pill + heading shown centered above the two-column block. */
  badge?: React.ReactNode
  sectionHeading?: React.ReactNode
  background?: string
}

const Tldr = ({
  image,
  imageAlt,
  imageWidth = 480,
  imageHeight = 480,
  eyebrow,
  showTldrLettering = false,
  heading,
  subheading,
  points,
  highlightClassName = 'font-700 not-italic text-orange bg-orange-200',
  badge,
  sectionHeading,
  background,
}: Props) => (
  <section className="bg-white pt-12 lg:pt-16 pb-6 lg:pb-10">
    {(badge || sectionHeading) && (
      <div className="pb-6 text-center px-5 lg:px-12 bg-white container mx-auto max-w-4xl">
        {badge && (
          <div className="inline-block rounded-full px-4 py-1 text-xs font-700 uppercase tracking-widest mb-5 bg-orange-200 text-orange">
            {badge}
          </div>
        )}
        {sectionHeading && (
          <h2 className="max-w-4xl mx-auto pb-3 text-2xl text-center text-grey lg:text-4xl font-600">
            {sectionHeading}
          </h2>
        )}
      </div>
    )}
    <div className={background}>
      <div className="container py-6 px-4 lg:px-8 gap-8 mx-auto divide-x items-center grid grid-cols-1 lg:grid-cols-2">
        <div className="ml-auto">
          <FastlyImage
            src={image}
            alt={imageAlt}
            width={imageWidth}
            height={imageHeight}
            className="rounded-2xl"
          />
        </div>

        <div className="border-none lg:border-grey-300 lg:border-dashed lg:pl-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-orange" />
            <span className="text-xs font-700 uppercase tracking-widest text-orange">
              {eyebrow}
            </span>
          </div>

          {(showTldrLettering || heading) && (
            <h3 className="font-700 lg:text-7xl md:text-5xl text-3xl text-grey leading-none">
              {showTldrLettering && (
                <>
                  <span className="font-serif">TL</span>
                  <span className="text-orange font-serif">;</span>
                  <span className="font-serif">DR</span>
                </>
              )}
              {heading && <>{heading}</>}
            </h3>
          )}

          {subheading && (
            <p className="text-grey-800 text-lg font-600 mb-2">{subheading}</p>
          )}

          <div>
            {points.map((point, i) => (
              <div key={i} className="py-4 border-t border-grey-200">
                <p className="text-grey-800 text-base leading-relaxed">
                  {point.prefix}
                  {point.highlight && (
                    <mark className={highlightClassName}>
                      {point.highlight}
                    </mark>
                  )}
                  {point.suffix}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
)

export default Tldr
